import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import { Filter, X, RotateCcw, Check } from 'lucide-react';

/**
 * FilterDrawer - Standardized right-side slide-over drawer for table and view filters.
 * Features consistent header badge count, smooth scrollable body, and sticky reset/apply footer.
 */
export default function FilterDrawer({
  open,
  onClose,
  title = 'Filters',
  subtitle = 'Refine table listings with attributes',
  activeCount = 0,
  onReset,
  onApply,
  resetLabel = 'Reset All',
  applyLabel = 'Apply Filters',
  isResetDisabled = false,
  isApplyDisabled = false,
  children,
  width = { xs: '100%', sm: 440, md: 480 },
}) {
  const theme = useTheme();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width,
            maxWidth: '100vw',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: 'primary.lighter',
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Filter size={18} strokeWidth={2.2} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={800} fontSize="1.05rem">
                {title}
              </Typography>
              {activeCount > 0 && (
                <Chip
                  size="small"
                  label={`${activeCount} Active`}
                  color="primary"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                />
              )}
            </Box>
            {subtitle && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        <IconButton
          aria-label="Close filter drawer"
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.primary' },
          }}
        >
          <X size={20} />
        </IconButton>
      </Box>

      {/* Filter Body with smooth scroll */}
      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        {children}
      </Box>

      {/* Fixed Sticky Footer Actions */}
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : alpha(theme.palette.grey[50], 0.9),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        {onReset && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onReset}
            disabled={isResetDisabled}
            startIcon={<RotateCcw size={16} />}
            sx={{
              py: 1.15,
              fontWeight: 600,
              borderRadius: 2,
            }}
          >
            {resetLabel}
          </Button>
        )}
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={() => {
            if (onApply) onApply();
            if (onClose) onClose();
          }}
          disabled={isApplyDisabled}
          startIcon={<Check size={16} />}
          sx={{
            py: 1.15,
            fontWeight: 700,
            borderRadius: 2,
          }}
        >
          {applyLabel}
        </Button>
      </Box>
    </Drawer>
  );
}
